(ns frontend.view.entity-viewers.components
  (:require
    [clojure.set :as set]
    [clojure.string :as string]
    [common.attribute :as attribute]
    [common.entity :as entity]
    [common.entity-utils :as eu]
    [common.features.plan :as plan]
    [common.localization :as loc]
    [common.schema.calculated-field :as calculated-field]
    [common.schema.constraints :as constraints]
    [common.schema.entities.pipeline :as pipeline]
    [common.schema.field :as field]
    [common.schema.registry :as registry]
    [common.util :as cu]
    [frontend.actions.account :as account-actions]
    [frontend.actions.entity :as entity-actions]
    [frontend.components.field-editor :as field-editor]
    [frontend.components.tab-strip :as tab-strip]
    [frontend.intelligence.icon :as intelligence-icon]
    [frontend.logging :as logging]
    [frontend.page-components.navbar-components.create-dropdown :as create-dropdown]
    [frontend.page-components.zap-toolbar :as zap-toolbar]
    [frontend.routing :as routing]
    [frontend.state.current-session :as session]
    [frontend.state.permissions :as f-permissions]
    [frontend.tenant-customizations :as tenant-customizations]
    [frontend.utils :as fu]
    [frontend.view.components.account :as account]
    [frontend.view.components.form.components.form-ratings :as form-ratings]
    [frontend.view.components.new-tags.view :as new-tags]
    [frontend.view.components.progress-indicator :as progress-indicator]
    [frontend.view.components.ratings :as ratings]
    [frontend.view.entity :as f-entity]
    [frontend.view.entity-viewers.enrichment :as enrichment]
    [frontend.view.entity-viewers.entities-editor :as entities-editor]
    [frontend.view.entity-viewers.entity-editor :as entity-editor]
    [frontend.view.entity-viewers.modules :as modules]
    [frontend.view.entity-viewers.modules.attachments :as attachments-module]
    [frontend.view.entity-viewers.modules.counterparty-diligences.view :as counterparty-diligences]
    [frontend.view.entity-viewers.modules.decisions.view :as decisions]
    [frontend.view.entity-viewers.modules.entity-esign.view :as entity-esign]
    [frontend.view.entity-viewers.modules.pre-exit-checks.view :as pec]
    [frontend.view.entity-viewers.modules.pre-investment-checks.view :as pic]
    [frontend.view.entity-viewers.modules.tasks.view :as tasks]
    [frontend.view.modals.account-select.view :as account-select]
    [frontend.view.modals.delete-confirmation.view :as delete-confirmation]
    [frontend.view.modals.entity-audit-trail.view :as audit-trail-modal]
    [frontend.view.modals.events :as modals]
    [frontend.view.modals.options-management.view :as options-management]
    [frontend.view.modals.ratings.deal :as ratings-deal]
    [frontend.view.modals.single-reference-relationship.view :as single-reference-relationship]
    [frontend.view.nev.events :as events]
    [potpuri.core :refer [fn->]]
    [reagent.core :as r]
    [ui.components.alert.view :as alert]
    [ui.components.button.view :as button]
    [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
    [ui.editors.labelled-input.view :as labelled-input]
    [ui.util :as uu]))

;;
;; entity-type based defaults:
;;

(def entity-type->view-id
  {:entity.type/company                 :company
   :entity.type/fof-asset               :fof-asset
   :entity.type/fund                    :fund
   :entity.type/fund-commitment         :fund-commitment
   :entity.type/fundraising-opportunity :fundraising-opportunity
   :entity.type/holding                 :holding
   :entity.type/investor                :investor
   :entity.type/oppo                    :deal
   :entity.type/other-asset             :other-asset
   :entity.type/person                  :person
   :entity.type/real-estate             :real-estate
   :entity.type/sales-opportunity       :sales-opportunity
   :entity.type/sale-agreement          :sale-agreement})

(defn default-view-id
  [{entity-type :entity/type}]
  (get entity-type->view-id entity-type (keyword (name entity-type))))

(defn default-editor-module-id
  [{entity-type :entity/type}]
  (get entity-type->view-id
       entity-type
       (keyword (str (name entity-type)
                     "-information"))))

(def entity-type->header-fields
  {:entity.type/company                 [:asset/description :company/next-development]
   :entity.type/fof-asset               [:asset/description]
   :entity.type/fund                    [:fund/description]
   :entity.type/fund-commitment         [:fund-commitment/overview]
   :entity.type/fundraising-opportunity [:fundraising-opportunity/description
                                         :fundraising-opportunity/next-steps]
   :entity.type/sales-opportunity       [:sales-opportunity/description :sales-opportunity/next-steps]
   :entity.type/holding                 [:holding/exit-plan :holding/recent-status]
   :entity.type/investor                [:investor/description]
   :entity.type/oppo                    [:oppo/case-note :oppo/status-and-next-steps]
   :entity.type/other-asset             [:asset/description]
   :entity.type/person                  [:person/description :person/notes]
   :entity.type/real-estate             [:asset/description]})

(defn default-header-fields
  [entity]
  (let [entity-type (:entity/type entity)
        header-fields (get entity-type->header-fields entity-type)
        capman_re? (and (= entity-type :entity.type/oppo)
                        (tenant-customizations/capman-is-real-estate-pipeline? (:oppo/pipeline
                                                                                 entity)))
        header-fields (if capman_re?
                        [:oppo/deal-description]
                        header-fields)]
    header-fields))

(def entity-type->remove-fields
  {:entity.type/company                 [:asset/description :company/website]
   :entity.type/fof-asset               [:asset/description]
   :entity.type/fund                    [:fund/name]
   :entity.type/fund-commitment         [:fund-commitment/name :fund-commitment/investor
                                         :fund-commitment/fund]
   :entity.type/fundraising-opportunity [:fundraising-opportunity/opportunity-name
                                         :fundraising-opportunity/fund-commitment
                                         :fundraising-opportunity/credit
                                         :fundraising-opportunity/investor
                                         :common/team-members]
   :entity.type/holding                 [:holding/name :holding/fund]
   :entity.type/investor                [:investor/name]
   :entity.type/oppo                    [:oppo/case-note :oppo/status-and-next-steps]
   :entity.type/other-asset             [:asset/name]
   :entity.type/person                  [:person/universal-email-address :person/avatar-url :person/notes
                                         :person/gender]
   :entity.type/real-estate             [:asset/name]})

;;fields hidden from entity-editor
(defn remove-editor-fields
  [entity]
  (let [entity-type (:entity/type entity)
        header-fields (default-header-fields entity)
        tenant-schema @session/tenant-schema
        tag-fields (field/entity-tag-fields tenant-schema entity-type)
        fields (concat tag-fields
                       header-fields
                       [:common/network-drive :common/named-network-drives]
                       (get entity-type->remove-fields entity-type))
        investor-fields
        (when (= entity-type :entity.type/investor)
          (merge
            {(:db/id entity) (concat [:investor/name :common/network-drive]
                                     (field/entity-tag-fields tenant-schema :entity.type/investor)
                                     header-fields)}
            (when-let [legal-entity-id (:investor/legal-entity entity)]
              (let [legal-entity @(r/track session/get-entity legal-entity-id)
                    entity-fields (->> legal-entity
                                       :entity/type
                                       (registry/get-schema tenant-schema)
                                       (map first)
                                       (set))]
                (if (= (:entity/type legal-entity) :entity.type/company)
                  {legal-entity-id (->> (set/difference entity-fields
                                                        #{:asset/street-address
                                                          :asset/street-address-2
                                                          :asset/country
                                                          :asset/city
                                                          :asset/zip-code
                                                          :company/website
                                                          :company/phone})
                                        (into []))}
                  {legal-entity-id (->> (set/difference entity-fields
                                                        #{:person/universal-email-address
                                                          :person/last-name
                                                          :person/first-name
                                                          :person/universal-phone-number
                                                          :person/location-address
                                                          :person/location-city
                                                          :person/location-country
                                                          :person/location-zip-code})
                                        (into []))})))))]
    (if (= entity-type :entity.type/investor)
      investor-fields
      fields)))

;;
;; Components:
;;

(defn add-team-member!
  [{:keys [entity-id accounts read-only?]}]
  (when-not read-only?
    (modals/open-modal!
      account-select/modal-key
      {:active-accounts accounts
       :header-text     (loc/tr :add-team-member)
       :on-select!      (fn [on-close! selected-account-id]

                          (let [entity (get @session/entities entity-id)
                                current-team (:common/team-members entity)
                                new-team (set/union current-team #{selected-account-id})]
                            ;;optimistic update:
                            (swap! session/entities assoc-in
                                   [entity-id :common/team-members]
                                   new-team)
                            (entity-actions/*->field-update!
                              entity-id
                              :common/team-members
                              new-team
                              (fn [_]
                                (on-close!))

                              (fn [err]
                                (let [entity (get @session/entities entity-id)
                                      team (:common/team-members entity)]
                                  ;;retract optimistic update:
                                  (when (= team new-team)
                                    (swap! session/entities assoc-in
                                           [entity-id :common/team-members]
                                           current-team))
                                  (logging/log "Error adding team-member : " err))))))})))

(defn remove-team-member!
  [{:keys [entity-id account-id]}]
  (let [entity (get @session/entities entity-id)
        current-team (:common/team-members entity)
        new-team (set/difference current-team #{account-id})]
    ;;optimistic update:
    (swap! session/entities assoc-in [entity-id :common/team-members] new-team)
    (entity-actions/*->field-update!
      entity-id
      :common/team-members
      new-team
      (fn [_])
      (fn [err]
        (let [entity (get @session/entities entity-id)
              team (:common/team-members entity)]
          ;;retract optimistic update:
          (when (= team new-team)
            (swap! session/entities assoc-in [entity-id :common/team-members] current-team))
          (logging/log "Error removing team-member : " err))))))

(defn member-tooltip
  [entity account-id]
  (let [account @(r/track session/get-entity account-id)
        type-text (loc/tr (:entity/type entity))
        account-name (when account
                       (entity/display-string account))]
    (loc/tr :team-member-tooltip [type-text account-name])))

(defn team-members-view
  [{:keys [entity pipeline-id entity-cursor owner-attribute members-attribute]}]
  (let [members (:common/team-members @entity-cursor)
        owner (get @entity-cursor owner-attribute)
        skip (set/union members #{owner})
        ;; list only accounts that are not already team-members or the owner for this entity:
        accounts (->> (session/active-accounts pipeline-id)
                      (remove (fn [a]
                                (some #{(:db/id a)} skip))))
        accounts (sort-by (fn-> entity/display-string str string/lower-case) accounts)
        read-only? (not (f-permissions/field-write-access? members-attribute
                                                           (:entity/type entity)
                                                           pipeline-id))]

    [:div
     {:class "flex items-center"}
     (doall
       (for [member members]
         ^{:key (str member)}
         [account/member-circle
          {:account-id   member
           :tooltip-text (member-tooltip @entity-cursor member)
           :on-remove!   (r/partial remove-team-member!
                                    {:entity-id  (:db/id @entity-cursor)
                                     :account-id member})}]))
     (when-not (or (empty? accounts)
                   (session/is-read-only-user?))
       [button/secondary-open
        {:tooltip  (loc/tr :add-team-member)
         :on-click (r/partial add-team-member!
                              {:entity-id  (:db/id @entity-cursor)
                               :accounts   accounts
                               :read-only? read-only?})}
        [:i.fal.fa-plus]])]))


(defn textarea-view
  [tenant-schema tenant entity enriched-entity attribute]
  (let [field-schema-orig (registry/get-field-schema tenant-schema attribute)
        field-schema
        (merge field-schema-orig
               {:access/type (f-permissions/access-to-field-long
                               attribute
                               (:entity/type entity)
                               (fu/pipeline-id entity))
                :editor/type (->> field-schema-orig
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})
        entity-atom (r/cursor session/entities [(:db/id entity)])
        value (fu/get-value tenant tenant-schema entity attribute)
        enriched-value (get enriched-entity attribute)
        read-only?
        (not (f-permissions/field-write-access? attribute
                                                (:entity/type entity)
                                                (fu/pipeline-id entity)))]
    (if-not field-schema-orig
      (logging/error "nev.components.textarea-view : Misconfiguration - no field schema : "
                     attribute)
      [field-editor/view
       (merge
         field-schema
         {:drag-options      {:hide-drag-area? true}
          :state-atom        state-atom
          :containing-entity entity
          :value             (or value enriched-value)
          :class-name        (fu/cs (:class-name field-schema)
                                    (when (and
                                            (fu/empty-value? value)
                                            (fu/empty-value? enriched-value))
                                      "empty"))
          :label             (loc/field-name attribute field-schema)
          :read-only?        read-only?
          :disabled?         read-only?
          :placeholder       (field/placeholder field-schema)
          ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
          :tools             [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy]
          :on-save!          (fn [attribute new-value on-success! on-error!]
                               ;;make sure local state is cleared when no changes:
                               (when (and (= new-value value)
                                          (fn? on-success!))
                                 (on-success!))
                               (when-not (= new-value value)
                                 ;;optimistic save to frontend db:
                                 (swap! entity-atom assoc attribute new-value)

                                 (entity-actions/*->field-update!
                                   (:db/id entity)
                                   attribute
                                   new-value
                                   (fn [_res]
                                     (when on-success!
                                       (on-success!)))
                                   (fn [err]
                                     ;;retract the optimistic save above
                                     (when (= new-value (get @entity-atom attribute))
                                       (swap! entity-atom assoc attribute value))
                                     (on-error! err)))))})])))



(defn website-view
  [{:keys [entity url-attribute]}]
  (let [tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema url-attribute)
        field-schema
        (merge field-schema
               {:access/type (f-permissions/access-to-field-long
                              url-attribute
                              (:entity/type entity)
                              (fu/pipeline-id entity))
                :editor/type (->> field-schema
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})]
    (fn [{:keys [entity url-attribute enriched-entity entity-atom]}]
      (let [website (or (get entity url-attribute)
                        (get enriched-entity url-attribute))
            read-only?
            (not (f-permissions/field-write-access? url-attribute
                                                    (:entity/type entity)
                                                    (fu/pipeline-id entity)))]
        [:span
         {:class (str "[&_ul.tools]:-top-1.5 [&_ul.tools]:pt-1.5 "
                      "[&_ul.tools]:pr-1.5 "
                      "[&_ul.tools]:pb-3 [&_ul.tools]:translate-x-full")}
         [field-editor/view
          (merge field-schema
                 {:drag-options      {:hide-drag-area? true}
                  :containing-entity entity
                  ;; state is passed  from outside so debugging is easier
                  :state-atom        state-atom
                  :value             website
                  :class-name        (fu/cs (:class-name field-schema)
                                            "entity-header-url-attribute"
                                            (when (empty? website)
                                              "empty")
                                            (str url-attribute))
                  :label             (or (->> field-schema
                                              :field/translations
                                              :en)
                                         (loc/field-name url-attribute field-schema)
                                         (loc/tr url-attribute))
                  :hide-label?       true
                  :read-only?        read-only?
                  :disabled?         read-only?
                  :placeholder       (field/placeholder field-schema)
                  ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
                  :tools             [:tool-item/cancel :tool-item/save :tool-item/edit
                                      :tool-item/copy :tool-item/open-url]
                  :on-save!          (fn [attribute new-website on-success! on-error!]
                                       ;;make sure local state is cleared when no changes:
                                       (when (and (= new-website website)
                                                  (fn? on-success!))
                                         (on-success!))
                                       (when-not (= new-website website)
                                         ;;optimistic save to frontend db:
                                         (swap! entity-atom assoc attribute new-website)
                                         (entity-actions/*->field-update!
                                          (:db/id entity)
                                          attribute
                                          new-website
                                          (fn [_res]
                                            (when on-success!
                                              (on-success!)))
                                          (fn [err]
                                             ;;retract the optimistic save above
                                            (when (= new-website (get @entity-atom attribute))
                                              (swap! entity-atom assoc attribute website))
                                            (on-error! err)))))})]]))))


(defn email-view
  [{:keys [entity email-attribute]}]
  (let [tenant-schema @session/tenant-schema
        field-schema (registry/get-field-schema tenant-schema email-attribute)
        field-schema
        (merge field-schema
               {:access/type (f-permissions/access-to-field-long
                               email-attribute
                               (:entity/type entity)
                               (fu/pipeline-id entity))
                :editor/type (->> field-schema
                                  :field/default-visibility
                                  :editor)})
        state-atom (r/atom {})]
    (fn [{:keys [entity email-attribute enriched-entity entity-atom read-only?]}]
      (let [email (or (get entity email-attribute)
                      (get enriched-entity email-attribute))
            read-only?
            (or read-only?
                (not (f-permissions/field-write-access? email-attribute
                                                        (:entity/type entity)
                                                        (fu/pipeline-id entity))))]
        [:span

         [field-editor/view
          (merge
            field-schema
            {:drag-options      {:hide-drag-area? true}
             :containing-entity entity
             :state-atom        state-atom                  ;; state is passed  from outside so debugging is easier
             :value             email
             :class-name        (fu/cs (:class-name field-schema)
                                       "entity-header-url-attribute"
                                       (when (empty? email)
                                         "empty")
                                       (str email-attribute))
             :label             (or (->> field-schema
                                         :field/translations
                                         :en)
                                    (loc/field-name email-attribute field-schema)
                                    (loc/tr email-attribute))
             :hide-label?       true
             :read-only?        read-only?
             :disabled?         read-only?
             :placeholder       (field/placeholder field-schema)
             ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
             :tools             [:tool-item/cancel :tool-item/save :tool-item/edit :tool-item/copy]
             :on-save!          (fn [attribute new-email on-success! on-error!]
                                  ;;make sure local state is cleared when no changes:
                                  (when (and (= new-email email)
                                             (fn? on-success!))
                                    (on-success!))
                                  (when-not (= new-email email)
                                    ;;optimistic save to frontend db:
                                    (swap! entity-atom assoc attribute new-email)
                                    (entity-actions/*->field-update!
                                      (:db/id entity)
                                      attribute
                                      new-email
                                      (fn [_res]
                                        (when on-success!
                                          (on-success!)))
                                      (fn [err]
                                        ;;retract the optimistic save above
                                        (when (= new-email (get @entity-atom attribute))
                                          (swap! entity-atom assoc attribute email))
                                        (on-error! err)))))})]]))))


(defn name-view
  [{:keys [entity read-only?]}]
  (let [entity-id (:db/id entity)
        tenant-schema @session/tenant-schema
        raw-input (r/atom nil)
        has-focus (r/atom false)
        edit-atom (r/atom false)
        error-atom (r/atom nil)
        entity-atom (r/cursor session/entities [entity-id])
        name-attribute (registry/resolve-name-attribute (:entity/type entity))
        name-schema (registry/get-field-schema tenant-schema name-attribute)
        read-only? (or read-only?
                       (:field/composite-attributes name-schema)
                       (not (f-permissions/field-write-access?
                              name-attribute
                              (:entity/type entity)
                              (fu/pipeline-id entity))))
        link-to-kyc-target? (= (:entity/type entity) :entity.type/kyc-profile)]
    (fn [entity]
      (let [original-name (entity/resolve-entity-name @entity-atom session/get-entity session/reverse-lookup)
            on-cancel! (fn [_]
                         (reset! raw-input nil))]
        [:span
         [:span
          {:class "w-full flex items-center gap-3"}
          (when (or @edit-atom @error-atom)
            [:div
             {:class "flex-1"}
             [labelled-input/view
              {:data-type :data.type/text-simple
               :value     (or (if (nil? @raw-input) original-name @raw-input) "")
               :on-change (fn [edited-str]
                            (reset! raw-input edited-str))
               :on-blur   (fn [_]
                            (let [new-name @raw-input]
                              (reset! has-focus false)
                              (reset! edit-atom false)
                              (reset! error-atom nil)
                              (when (and new-name
                                         (not= new-name original-name))
                                (swap! entity-atom assoc name-attribute new-name)
                                (entity-actions/*->field-update!
                                  entity-id
                                  name-attribute
                                  new-name
                                  (fn [_res]
                                    #_(when on-success!
                                        (on-success!)))
                                  (fn [err]
                                    ;;retract the optimistic save above
                                    (js/console.log "ERROR saving name " err)
                                    (when (= new-name (get @entity-atom name-attribute))
                                      (swap! entity-atom assoc name-attribute original-name))
                                    (reset! error-atom "Duplicate value")
                                    (reset! edit-atom true))))))
               :disabled  read-only?
               :on-focus  #(reset! has-focus true)}]

             ;;  [:input
             ;;   {:class-name  (fu/cs :nev-heading-entity-name-input)
             ;;    :ref         (fn [dom-ref]
             ;;                   (when (and dom-ref
             ;;                              (not= dom-ref (:dom-ref @focus-state)))
             ;;                     (swap! focus-state assoc :dom-ref dom-ref)
             ;;                     (.focus dom-ref)))
             ;;    :value       (or (if (nil? @raw-input) original-name @raw-input) "")
             ;;    :on-change   (fn [e]
             ;;                   (let [input (-> e
             ;;                                   .-target
             ;;                                   .-value)]
             ;;                     (reset! raw-input input)))
             ;;    :disabled    read-only?
             ;;    :on-focus    #(reset! has-focus true)
             ;;    :on-blur     #(do (reset! has-focus false)
             ;;                      (reset! edit-atom false)
             ;;                      (reset! error-atom nil)
             ;;                      (when (and @raw-input
             ;;                                 (not= @raw-input original-name))
             ;;                        (on-name-change! @raw-input)))
             ;;    :on-key-down on-key-down!}]
             (when-let [error @error-atom]
               [:span
                {:class "text-danger-light-text text-xs"}
                error])])

          (when-not @edit-atom
            [:label
             {:class "text-xl font-bold"}
             (or (if (nil? @raw-input) original-name @raw-input) "")])

          (when (and (not @edit-atom) link-to-kyc-target?)
            [:span
             {:class-name (uu/cm "cursor-pointer"
                                 "group-hover:visible")}
             [:i.fal.fa-external-link
              {:on-click (fn [_]
                           (routing/open-entity (:kyc-profile/target @entity-atom)))}]])

          (when-not read-only?
            [:span
             {:class-name (uu/cm "cursor-pointer"
                                 "group-hover:visible"
                                 (when @edit-atom
                                   ""))}
             (if-not @edit-atom
               [:i.fal.fa-edit
                {:on-click #(reset! edit-atom true)}]
               [:i.fal.fa-undo
                {:on-mouse-down on-cancel!}])])]


         (when (= :holding.exit-status/exited (:holding/exit-status entity))
           [:span
            {:class-name "holdings-exited"}
            [:i.fal.fa-sign-out] (str " " (loc/tr :holding.exit-status/exited))])]))))


(defn ratings-view
  [{:keys [entity entity-cursor pipeline-id ratings-attribute tooltip-text entity-id]}]
  (let [read-only? (not (f-permissions/field-write-access? ratings-attribute
                                                           (:entity/type entity)
                                                           pipeline-id))
        ratings (get @entity-cursor ratings-attribute)]
    [button/helper-button
     {:tooltip tooltip-text
      :on-click
      (fn [_]
        (when-not read-only?
          (modals/open-modal!
            ratings-deal/modal-key
            {:label         tooltip-text
             :accounts      (session/active-accounts-with-pipeline-access pipeline-id)
             :value         ratings
             :step          form-ratings/step
             :scale         form-ratings/scale
             :show-buttons? true
             :on-change!    (fn [_])
             ;; do not send live updates - wait for submit button

             :on-submit!    (fn [ratings-after]
                              (when-not (= ratings-after ratings)
                                ;;optimistic save to frontend db:
                                (swap! entity-cursor assoc ratings-attribute ratings-after)
                                (entity-actions/*->field-update!
                                  entity-id
                                  ratings-attribute
                                  ratings-after
                                  (fn [_res])
                                  (fn [_err]
                                    ;;retract the optimistic save above
                                    (when (= ratings-after (get @entity-cursor ratings-attribute))
                                      (swap! entity-cursor assoc ratings-attribute ratings)
                                      (deref entity-cursor))
                                    (logging/log "Show ratings update failed!")))
                                (deref entity-cursor)))})))}
     [:span
      (when-not (empty? ratings)
        (loc/tr :ratings-value))]
     [:span
      (str (ratings/round-average (if (not-empty ratings)
                                    (/ (reduce + (map #(* ratings/scale-count %) (vals ratings)))
                                       (count ratings))
                                    (loc/tr :ratings-no-value-in-deal))))]]))

(defn select-owner!
  [{:keys [entity-cursor entity-id owner-attribute pipeline-id]}]
  (let [accounts (->> (session/active-accounts pipeline-id)
                      (remove (fn [a]
                                (= (:db/id a) (get @entity-cursor owner-attribute)))))]
    (when-not (empty? accounts)
      (modals/open-modal!
        account-select/modal-key
        {:active-accounts accounts
         :header-text     (loc/tr :select-owner)
         :on-select!      (fn [on-close! selected-account-id]
                            (let [entity (get @session/entities entity-id)
                                  current-owner (get entity owner-attribute)]
                              ;;optimistic update:
                              (swap! entity-cursor assoc owner-attribute selected-account-id)
                              (entity-actions/*->field-update!
                                entity-id
                                owner-attribute
                                selected-account-id
                                (fn [_]
                                  (on-close!))

                                (fn [err]
                                  ;;retract optimistic update:
                                  (when (= (get @entity-cursor owner-attribute) selected-account-id)
                                    (swap! entity-cursor assoc owner-attribute current-owner))
                                  (logging/log "Error updating owner : " err)
                                  (deref entity-cursor)))

                              (deref entity-cursor)))}))))

(defn display
  [entity-id]
  (let [entity @(r/track session/get-entity entity-id)]
    (when entity
      (entity/display-string entity))))

(defn owner-stage-rating-module
  [{:keys [entity-cursor entity
           entity-stage-attribute stage-entity-type stage-pipeline-id stage-pipeline-attribute
           ratings-attribute owner-attribute members-attribute]}]
  (let [current-stage-id (get entity entity-stage-attribute)
        sorted-stages (->> @(r/track session/get-entities stage-entity-type)
                           (filter (fn [s]
                                     (= stage-pipeline-id (get s stage-pipeline-attribute))))
                           (sort-by :common/ord))]
    [:div
     {:class "grid grid-cols-3 items-center"}
     [:div
      {:class "flex items-center"}
      (when (f-permissions/field-read-access? owner-attribute
                                              (:entity/type entity)
                                              stage-pipeline-id)
        [account/account-circle (get entity owner-attribute)
         (if (session/is-read-only-user?)
           identity
           (r/partial select-owner!
                      {:entity-cursor   entity-cursor
                       :entity-id       (:db/id entity)
                       :owner-attribute owner-attribute
                       :pipeline-id     stage-pipeline-id}))
         (str (loc/tr :common/owner)
              ": "
              (let [owner-id (get @entity-cursor owner-attribute)
                    owner-name @(r/track display owner-id)]
                owner-name))])

      (when (f-permissions/field-read-access? members-attribute
                                              (:entity/type entity)
                                              stage-pipeline-id)
        [team-members-view
         {:entity            entity
          :pipeline-id       stage-pipeline-id
          :entity-cursor     entity-cursor
          :owner-attribute   owner-attribute
          :members-attribute members-attribute}])]

     (when (seq sorted-stages)
       [:span
        [progress-indicator/view
         {:stages
          (for [stage sorted-stages]
            (let [stage-name (entity/display-string stage)]
              {:kw           (:db/id stage)
               :tooltip-text (cond (= (:db/id stage) current-stage-id)
                                   stage-name
                                   (session/is-read-only-user?)
                                   stage-name
                                   :else
                                   (loc/tr :move-to-stage [stage-name]))
               :on-click!    (when-not (session/is-read-only-user?)
                               (fn [_]
                                 (let [new-stage-id (:db/id stage)]
                                   (when-not (= new-stage-id current-stage-id)
                                     (f-entity/attempt-move-to-stage!
                                      {:opportunity entity
                                       :target-stage stage
                                       :before-save!
                                       (fn []
                                         (swap! entity-cursor assoc
                                                entity-stage-attribute new-stage-id)
                                         (deref entity-cursor))
                                       :on-error!
                                       (fn [_err]
                                         (when (= new-stage-id
                                                  (get @entity-cursor entity-stage-attribute))
                                           (swap! entity-cursor assoc
                                                  entity-stage-attribute
                                                  current-stage-id)
                                           (deref entity-cursor))
                                         (logging/log "Set stage update failed!"))})))))}))
          :current-stage current-stage-id}]])
     (if (and ratings-attribute
              (not (session/is-guest?))
              (f-permissions/field-read-access? ratings-attribute
                                                (:entity/type entity)
                                                stage-pipeline-id))
       [:span
        {:class "flex justify-end"}
        [ratings-view
         {:entity            entity
          :entity-cursor     entity-cursor
          :pipeline-id       stage-pipeline-id
          :ratings-attribute ratings-attribute
          :entity-id         (:db/id entity)
          :tooltip-text      (if (not-empty (get entity ratings-attribute))
                               (loc/tr :ratings-tooltip-modify [(entity/display-string entity)])
                               (loc/tr :ratings-tooltip-add-value
                                 [(entity/display-string entity)]))}]]
       ;; empty container for layout:
       [:span.flex.ratings__container])]))

(defn field-editor
  [{:keys [tenant attribute state-atom entity-template-lookup entity enriched-entity tools]}]
  (let [tenant-schema @session/tenant-schema
        entity-type (:entity/type entity)
        field-schema (get entity-template-lookup attribute)
        resolve-value (fn []
                        (let [entity @state-atom]
                          (fu/get-value tenant tenant-schema entity attribute)))
        pipeline-attribute (pipeline/pipeline-key entity-type)
        pipeline-id (or (get @state-atom pipeline-attribute)
                        (get entity pipeline-attribute))
        read-only? (or (session/is-read-only-user?)
                       (not
                         (f-permissions/field-write-access? attribute entity-type pipeline-id)))
        value (resolve-value)
        intelligence-value (get enriched-entity attribute)
        no-value? (and (nil? value)
                       (fu/empty-value? intelligence-value))
        is-intelligence-value? (and (nil? value)
                                    (contains? enriched-entity attribute))]
    [field-editor/view
     (merge
       field-schema
       {:attribute          attribute
        :state-atom         state-atom                      ;; state is passed  from outside so debugging is easier
        :value              (if is-intelligence-value? intelligence-value value)
        :containing-entity  entity
        :class-name         (fu/cs (:class-name field-schema)
                                   (when no-value? "empty"))
        :entity-types-atom  session/entity-types
        :entity-type        entity-type
        :entity-unique-name (entity/unique-name-or-id entity)
        ;; TODO: take language from user / tenant config -> default to :en
        ;; if only :en in field/translations use :en
        :label              (or (->> field-schema
                                     :field/translations
                                     :en)
                                (loc/field-name attribute field-schema)
                                (loc/tr attribute))             ;; we select label outside field-editor so we can customize label if needed
        :read-only?         read-only?

        ;:field-access [:x :w :r] ;; TODO: Fairly urgent - change the way field editors access is resolved everywhere so that the access is passed from outside
        :open-settings!     (fn []
                              (modals/open-modal! options-management/modal-key
                                                  {:pipeline-attribute pipeline-attribute
                                                   :selected-field     attribute}))
        :disabled?          read-only?
        :placeholder        (field/placeholder field-schema)
        ;; tools are configured by need - all handlers are inside as currently the tools have no side effects
        :tools              (or tools
                                (concat [:tool-item/cancel :tool-item/save :tool-item/edit
                                         :tool-item/copy :tool-item/open-url :tool-item/clear
                                         :tool-item/debug]
                                        (when (and (session/is-admin?)
                                                   (->> field-schema
                                                        :named-value-attribute))
                                          [:tool-item/settings])))
        :validate!          (fn [attribute value]
                              (let [schema (get entity-template-lookup attribute)
                                    field-constraints (:field/constraints schema)
                                    errors (constraints/validate field-constraints
                                                                 (:db/id entity)
                                                                 value
                                                                 entity
                                                                 (partial session/unique-check
                                                                          false
                                                                          (partial
                                                                            routing/open-entity))
                                                                 loc/tr
                                                                 logging/warn)]
                                errors))
        :error-text         nil                             ;; TODO: use editor-atom to store validation and save errors
        :on-save!           (fn [attribute new-value on-success! on-error!]
                              (let [value (resolve-value)]
                                ;;make sure local state is cleared when no changes:
                                (if (and (= new-value value)
                                         (fn? on-success!))
                                  (on-success!)
                                  (when-not (= new-value value)
                                    (if-let [create-value (:new-option new-value)]
                                      (entity-actions/*->field-update-with-new-entity!
                                        (:db/id entity)
                                        attribute
                                        value
                                        create-value
                                        (fn [_res]
                                          (when on-success! (on-success!)))
                                        (fn [err]
                                          (when on-error! (on-error! err))))
                                      (do
                                        ;;optimistic save to frontend db:
                                        (swap! state-atom assoc attribute new-value)
                                        (deref state-atom)
                                        ;; Index at once. A listing that shows this entity
                                        ;; reads OpenSearch, and the completion tells it to refresh.
                                        (entity-actions/*->field-update-instant-indexing!
                                          (:db/id entity)
                                          attribute
                                          new-value
                                          (fn [_res]
                                            (when on-success!
                                              (on-success!)))
                                          (fn [err]
                                            ;;retract the optimistic save above
                                            (when (= new-value (get @state-atom attribute))
                                              (swap! state-atom assoc attribute value)
                                              (deref state-atom))
                                            (on-error! err)))))))))}

       (when is-intelligence-value?                         ;; we decide source outside
         {:source-options {:source-icon-fn intelligence-icon/view}})
       (when-let [secondary-attribute (:field/secondary-attribute field-schema)] ;; secondary attribute
         {:secondary-view-args
          (let [sec-templ (get entity-template-lookup secondary-attribute)
                secondary-field-schema (registry/get-field-schema tenant-schema secondary-attribute)]
            (merge
              sec-templ
              {:attribute  secondary-attribute
               :state-atom state-atom
               ;; TODO: fix composite-attribute to check calculated field instead
               :value      (if (:field/composite-attributes secondary-field-schema)
                             (when-let [calc-function (:field/calculated-field
                                                        secondary-field-schema)]
                               (calculated-field/calculate
                                 {:calc-fn          calc-function
                                  :entity-data      entity
                                  :entity-lookup    session/get-entity
                                  :reverse-lookup   session/reverse-lookup
                                  :tr               loc/tr
                                  :bigdec->float    fu/bigdec->float
                                  :resolve-value    (partial eu/get-value tenant tenant-schema)
                                  :float->bigdec    fu/float->bigdec
                                  :get-field-schema (partial registry/get-field-schema tenant-schema)
                                  :display-string   entity/display-string}))
                             (or (get @state-atom secondary-attribute)
                                 (when (= (:field/data-type sec-templ) :data.type/currency-symbol)
                                   (:tenant/default-currency @session/tenant))))
               :label      (loc/tr secondary-attribute)         ;; when we have custom dynamic fields the labels come from tenant configuration
               :error-text nil}))}))]))

(defn header-module
  [{:keys [entity
           entity-atom
           enriched-entity
           entity-template-lookup
           selected-fields
           field-rows
           url-attribute
           email-attribute
           read-only-email?
           related-tags-entity-id
           pipeline-id
           logo-url]}]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema]
    [:div
     [:div
      {:class "p-4 mb-4 bg-base-light border border-solid border-base-light-border-paler rounded transition"}
      [:div
       [:div
        [:div
         [:div
          {:class-name "flex gap-2 justify-between align-start flex-wrap"}
          [:div
           {:class "flex w-5/12 gap-4 items-base relative"}
           [:span
            {:class "inline-block"}
            (if logo-url
              [:img {:class "w-10"
                     :src   (fu/sanitize-img-url logo-url)}]
              [:i.text-xl {:class (uu/cm :fas (entity/default-icon entity))}])]
           [:div
            {:class "w-full"}
            [:div
             [name-view {:entity entity}]
             (when url-attribute
               [website-view
                {:entity          entity
                 :enriched-entity enriched-entity
                 :url-attribute   url-attribute
                 :entity-atom     entity-atom}])
             (when email-attribute
               [email-view
                {:entity          entity
                 :enriched-entity enriched-entity
                 :read-only?      read-only-email?
                 :email-attribute email-attribute
                 :entity-atom     entity-atom}])]]]
          [:div
           {:class "flex justify-end md:4/12 lg:w-3/12"}
           [:div
            [:div
             [new-tags/view
              {:entity-id        (:db/id entity)
               :entity-type      (:entity/type entity)
               :edit-entity-id   (:db/id entity)
               :edit-entity-type (:entity/type entity)
               :pipeline-id      pipeline-id
               :icon             :fa-tags
               :title            (loc/tr :-tags [(loc/tr (:entity/type entity))])}]
             (when-let [related-tags-entity @(r/track session/get-entity related-tags-entity-id)]
               [new-tags/view
                {:entity-id        (:db/id related-tags-entity)
                 :entity-type      (:entity/type related-tags-entity)
                 :edit-entity-id   (:db/id related-tags-entity)
                 :pipeline-id      pipeline-id
                 :edit-entity-type (:entity/type related-tags-entity)
                 :title            (loc/tr :-tags [(loc/tr (:entity/type related-tags-entity))])}])]]]]]
        (->>
          selected-fields
          (filter (fn [attribute]
                    (let [visible? (and attribute
                                        (some #{attribute} (:tenant/fields-in-use @session/tenant))
                                        (f-permissions/field-read-access?
                                          attribute
                                          (:entity/type entity)
                                          (fu/pipeline-id entity))
                                        (let [fs (registry/get-field-schema tenant-schema attribute)]
                                          (some #{(:field/data-type fs)}
                                                [:data.type/text :data.type/text-multiline
                                                 :data.type/text-richtext])))]
                      visible?)))
          (partition 2 2 [nil])
          (map-indexed
            (fn [partition-idx chunk-attributes]
              (let [non-padded-attributes (remove nil? chunk-attributes)]
                (case (count non-padded-attributes)
                  2
                  [:div
                   {:key        (str "entity-nev-" (:db/id entity) "-header-" partition-idx)
                    :class-name "grid grid-cols-2 gap-4"}
                   [:div
                    [textarea-view tenant-schema tenant entity enriched-entity
                     (first non-padded-attributes)]]
                   [:div
                    [textarea-view tenant-schema tenant entity enriched-entity
                     (second non-padded-attributes)]]]
                  1
                  [:div
                   {:class-name "grid gap-4"
                    :key        (str "entity-nev-" (:db/id entity) "-header-" partition-idx)}
                   [:div
                    [textarea-view tenant-schema tenant entity enriched-entity
                     (first non-padded-attributes)]]]
                  nil))))
          (remove nil?)
          (doall))
        (->>
          field-rows
          (map-indexed
            (fn [row-idx row]
              (let [attributes (->> row
                                    (filter (fn [attribute]
                                              (and attribute
                                                   (f-permissions/field-read-access?
                                                     attribute
                                                     (:entity/type entity)
                                                     (fu/pipeline-id entity))))))]
                (case (count attributes)
                  2 [:div
                     {:key        (str "entity-nev-" (:db/id entity) "-header-row-" row-idx)
                      :class-name "grid grid-cols-2 gap-4"}
                     [:div
                      [field-editor
                       {:attribute              (first attributes)
                        :tenant                 tenant
                        :entity                 entity
                        :enriched-entity        enriched-entity
                        :state-atom             entity-atom
                        :entity-template-lookup entity-template-lookup}]]

                     [:div
                      [field-editor
                       {:attribute              (second attributes)
                        :tenant                 tenant
                        :entity                 entity
                        :enriched-entity        enriched-entity
                        :state-atom             entity-atom
                        :entity-template-lookup entity-template-lookup}]]]

                  1 [:div
                     {:key        (str "entity-nev-" (:db/id entity) "-header-row-" row-idx)
                      :class-name "grid gap-4"}
                     [:div
                      [field-editor
                       {:attribute              (first attributes)
                        :tenant                 tenant
                        :entity                 entity
                        :enriched-entity        enriched-entity
                        :state-atom             entity-atom
                        :entity-template-lookup entity-template-lookup}]]]

                  nil))))
          (remove empty?)
          (doall))]]]]))

(defn module-collapsed-path
  [view-id module-id]
  [view-id :modules :module-config module-id :collapsed?])

(defn opportunity-creator-dropdown
  "Renders an 'Add new' nested dropdown (pipeline → stage) for creating an
   opportunity of `:entity-type` (deal / sales-oppo / exit-oppo / froppo)
   with `:initial-target` preselected and target-locked. Intended as the
   `:add-new-content` value of `association-module-config` — callers gate on
   the relevant creation-flow predicate, e.g.
   `(when (session/deal-creation-flow?) [opportunity-creator-dropdown ...])`."
  [{:keys [entity-type initial-target]}]
  [:div.related-entities__add.field-editor__content__field-input.w-100
   [dropdown-menu/view
    {:trigger [dropdown-menu/button-trigger-hollow
               (loc/tr :add-new)
               [:i.fal.fa-angle-right]]
     :props {:side "right"
             :align "start"}
     :items (create-dropdown/opportunity-creator-items
             {:entity-type entity-type
              :open-creator-args {:initial-target initial-target
                                  :target-locked? true}})}]])

(defn association-module-config
  [{:keys [module-label
           module-id
           view-id
           nev-entity-type
           target-entity-type
           associated-entities
           save-collapsed-status!
           new-entity-start-value
           dispatch-on-create
           add-new-content
           add-new-button?]}]

  (when (or (not (session/is-read-only-user?))
            (seq associated-entities))
    {:module-id              module-id
     :header                 module-label
     :collapsed?             @(r/track session/get-view-config
                                       (module-collapsed-path view-id module-id))
     :start-collapsed?       (empty? associated-entities)
     :save-module-collapsed! save-collapsed-status!
     :content                [:div
                              {:class "mb-0 mt-2 px-2 text-sm"}
                              (for [associated-entity associated-entities
                                    :let [{:keys [db/id]} associated-entity]]
                                ^{:key (str (name target-entity-type) "-" (name nev-entity-type) "-" id)}
                                [:div
                                 {:class    "flex flex-col gap-2 w-full"
                                  :on-click (fn [_e]
                                              (routing/open-entity (:db/id associated-entity)))}
                                 [:div
                                  {:class "px-3 py-2 flex justify-between gap-2 items-center bg-base-light border border-solid border-base-light-border-paler hover:border-base-light-border-accent rounded cursor-pointer"}
                                  [:span
                                   {:class "flex gap-2 items-center"}
                                   [:i
                                    {:class-name (fu/cs :fal (entity/default-icon associated-entity))}]
                                   [:span
                                    {:class "text-semibold"}
                                    (entity/display-string associated-entity)]]]])
                              (when (and add-new-button?
                                         (not (session/is-read-only-user?)))
                                (or add-new-content
                                    [:div.related-entities__add.field-editor__content__field-input.w-100
                                     [button/secondary-open
                                      {:class    "underline"
                                       :on-click (fn [_]
                                                   (f-entity/open-creator! target-entity-type
                                                                           {:start-value
                                                                            new-entity-start-value
                                                                            :dispatch-on-create
                                                                            dispatch-on-create}))}
                                      (loc/tr :add-new)]]))]}))

(defn notification-bar
  [notification-atom]
  [:div
   {:class "max-w-[640px] mx-auto"}
   [alert/info
    {:on-close (fn [_]
                 (reset! notification-atom nil)
                 ;; deref to propagate changes if cursor
                 @notification-atom)}
    [:i.fal.fa-exclamation-triangle.mr-2]
    @notification-atom]])

(defn error-bar
  [error-atom]
  [:div
   {:class "max-w-[640px] mx-auto"}
   [alert/danger
    {:on-close (fn [_]
                 (reset! error-atom nil)
                 ;; deref to propagate changes if cursor
                 @error-atom)}
    [:i.fal.fa-exclamation-triangle.mr-2]
    @error-atom]])

(defn save-module-collapsed!
  [config-path _view-id _module-id collapsed? on-success! on-error!]
  (let [msg {:path      config-path
             :new-value collapsed?}]
    (account-actions/*->update-view-config msg on-success! on-error!)))

(defn attachments-module
  [view-id module-id entity]
  (let [config-path [view-id :modules :module-config module-id :collapsed?]
        attachment-ids (:common/attachments entity)
        attachments (->> attachment-ids
                         (keep session/get-entity)
                         (seq))]
    (when (or (not (session/is-read-only-user?))
              attachments)
      {:module-id              module-id
       :header                 (loc/tr :nev-module/attachments)
       :collapsed?             @(r/track session/get-view-config config-path)
       :save-module-collapsed! (r/partial save-module-collapsed! config-path)
       ;:header-tools     [attachments-module/header-view entity]
       :start-collapsed?       (empty? (:common/attachments entity))
       :content                [:div
                                [attachments-module/view
                                 {:entity-id   (:db/id entity)
                                  :attachments attachments}]]})))

(defn pre-investment-checks-module
  [{:keys [view-id module-id module-label entity expanded-columns]}]
  (let [tenant-schema @session/tenant-schema
        entities @session/entities
        config-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        expanded? (when expanded-columns
                    @(r/track session/get-view-config expanded-path))]
    {:module-id              module-id
     :header                 module-label
     :header-tools           [pic/header-view tenant-schema entity]
     :collapsed?             @(r/track session/get-view-config expanded-path)
     :expanded?              expanded?
     :save-module-expanded!  (when expanded-columns
                               (r/partial save-module-collapsed! expanded-path))
     :start-collapsed?       true
     :save-module-collapsed! (r/partial save-module-collapsed! config-path)
     :content                [pic/view tenant-schema entities entity]}))

(defn pre-exit-checks-module
  [{:keys [view-id module-id module-label entity expanded-columns]}]
  (let [tenant-schema @session/tenant-schema
        entities @session/entities
        config-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        expanded? (when expanded-columns
                    @(r/track session/get-view-config expanded-path))]
    {:module-id              module-id
     :header                 module-label
     :header-tools           [pec/header-view tenant-schema entity]
     :collapsed?             @(r/track session/get-view-config expanded-path)
     :expanded?              expanded?
     :save-module-expanded!  (when expanded-columns
                               (r/partial save-module-collapsed! expanded-path))
     :start-collapsed?       true
     :save-module-collapsed! (r/partial save-module-collapsed! config-path)
     :content                [pec/view
                              {:tenant-schema tenant-schema
                               :entities      entities
                               :entity        entity}]}))

(defn decisions-module
  [{:keys [view-id module-id module-label entity expanded-columns]}]
  (let [config-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        expanded? (when expanded-columns
                    @(r/track session/get-view-config expanded-path))]
    {:module-id              module-id
     :header                 module-label
     :header-tools           [decisions/header-view entity]
     :collapsed?             @(r/track session/get-view-config expanded-path)
     :expanded?              expanded?
     :save-module-expanded!  (when expanded-columns
                               (r/partial save-module-collapsed! expanded-path))
     :start-collapsed?       true
     :save-module-collapsed! (r/partial save-module-collapsed! config-path)
     :content                [decisions/view entity]}))

(defn counterparty-diligences-module
  [{:keys [view-id module-id module-label entity]}]
  (let [collapsed-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        expanded? @(r/track session/get-view-config expanded-path)]
    {:module-id              module-id
     :header                 module-label
     :header-tools           [counterparty-diligences/header-view
                              {:entity entity}]
     :collapsed?             @(r/track session/get-view-config collapsed-path)
     :expanded?              expanded?
     :save-module-expanded!  nil
     :save-module-collapsed! (r/partial save-module-collapsed! collapsed-path)
     :content                [counterparty-diligences/view
                              {:entity entity}]}))

(defn tasks-module
  [{:keys [view-id module-id module-label entity expanded-columns hide-drag-handle?]}]
  (let [config-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        expanded? @(r/track session/get-view-config expanded-path)]
    {:module-id              module-id
     :header                 module-label
     :collapsed?             @(r/track session/get-view-config config-path)
     :expanded?              expanded?
     :save-module-expanded!  (when expanded-columns
                               (r/partial save-module-collapsed! expanded-path))
     :start-collapsed?       false
     :save-module-collapsed! (r/partial save-module-collapsed! config-path)
     :hide-drag-handle?      hide-drag-handle?
     :header-tools           [tasks/header-view (:db/id entity)]
     :content                [tasks/view (:db/id entity)]}))



(defn entity-esign-module
  [{:keys [view-id module-id module-label entity]}]
  (let [module-collapsed-path [view-id :modules :module-config module-id :collapsed?]]
    {:module-id              module-id
     :header                 module-label
     :header-tools           [entity-esign/header-view
                              {:entity entity}]
     :collapsed?             @(r/track session/get-view-config module-collapsed-path)
     :expanded?              false
     :save-module-expanded!  nil
     :save-module-collapsed! (partial events/save-module-collapsed! (:entity/type entity))
     :content                [entity-esign/view
                              {:entity entity}]}))

(defn entity-listing-module
  [{:keys [view-id module-id module-title shown-entities shown-columns
           expanded-columns show-totals? custom-footer
           icon on-icon-click!]
    :or   {icon           "fa-external-link"
           on-icon-click! (fn [entity _event]
                            (routing/open-entity (:db/id entity)))}}]
  (let [tenant-schema @session/tenant-schema
        config-path [view-id :modules :module-config module-id :collapsed?]
        expanded-path [view-id :modules :module-config module-id :expanded?]
        tenant @session/tenant
        expanded? (when expanded-columns
                    @(r/track session/get-view-config expanded-path))]
    {:module-id              module-id
     :header                 module-title
     :collapsed?             @(r/track session/get-view-config config-path)
     :expanded?              expanded?
     :save-module-expanded!  (when expanded-columns
                               (r/partial save-module-collapsed! expanded-path))
     :start-collapsed?       (empty? shown-entities)
     :save-module-collapsed! (r/partial save-module-collapsed! config-path)
     :content
     (fn [_module]
       [:div
        {:class (fu/cs "table-responsive")}
        [:table.zapflow-table.table.table-hover
         [:thead
          [:tr
           [:th.btn-short-cell ""]
           (->>
             (if expanded?
               expanded-columns
               shown-columns)
             (map (fn [c]
                    ^{:key (fu/format "%s%s-heading-%s" (name view-id) (name module-id) (:key c))}
                    [:th
                     (let [attrib (:key c)
                           fs (registry/get-field-schema tenant-schema attrib)
                           label (attribute/attribute-label tenant-schema attrib)]
                       (if (= :data.type/currency-symbol (:field/data-type fs))
                         [:i.fal.fa-coin]
                         label))]))
             (doall))]]
         [:tbody
          (->>
            shown-entities
            (map
              (fn [entity]
                (let [k (fu/format "%s%s-row-%s" (name view-id) (name module-id) (:db/id entity))]
                  ^{:key k}
                  [:tr
                   [:td.btn-short-cell
                    [:i
                     {:class-name (fu/cs "fal" icon)
                      :on-click   (r/partial on-icon-click! entity)}]]
                   (->> (if expanded?
                          expanded-columns
                          shown-columns)
                        (map (fn [c]
                               (let [attrib (:key c)
                                     fs (registry/get-field-schema tenant-schema attrib)
                                     v (fu/display-data tenant-schema tenant attrib fs entity)]
                                 [:td
                                  {:key   (str k "-" attrib)
                                   :style {:text-align
                                           (case (:field/data-type fs)
                                             :data.type/currency-value "right"
                                             :data.type/number "right"
                                             :data.type/integer "right"
                                             :data.type/percentage "right"
                                             :data.type/multiplier "right"
                                             :data.type/long "right"
                                             "left")}}
                                  (str v)])))
                        (doall))])))
            (doall))]
         (cond
           (fn? custom-footer)
           (custom-footer (if expanded? expanded-columns shown-columns) shown-entities)

           show-totals?
           [:tfoot
            [:tr
             [:td ""]
             (->>
               (if expanded?
                 expanded-columns
                 shown-columns)
               (map
                 (fn [c]
                   (let [attrib (:key c)
                         fs (registry/get-field-schema tenant-schema attrib)
                         k
                         (fu/format "%s%s-row-%s-%s" (name view-id) (name module-id) "total" attrib)]
                     ^{:key k}
                     [:td
                      {:style {:font-weight "bold"
                               :text-align
                               (case (:field/data-type fs)
                                 :data.type/currency-value "right"
                                 :data.type/number "right"
                                 :data.type/integer "right"
                                 :data.type/percentage "right"
                                 :data.type/multiplier "right"
                                 :data.type/long "right"
                                 "left")}}
                      (when-let [agg-fn (:field/aggregate-total-fn fs)]
                        (let [values (->> shown-entities
                                          (mapv
                                            (fn [e]
                                              (fu/get-single-value- tenant-schema tenant e attrib))))
                              total (agg-fn values)]
                          (fu/display-data tenant-schema
                                           tenant
                                           attrib
                                           {:field/data-type          (:field/data-type fs)
                                            :field/default-visibility (:field/default-visibility fs)}
                                           {attrib total}
                                           :passed-value
                                           total)))])))
               (doall))]])]])}))

(defn investor-legal-entities-table
  [related-entities entity-type]
  [:div.table__wrapper.table-responsive.mt-3
   [:div.infobox.infobox--warning.mb-3.align-items-center
    [:i.fas.fa-info-circle.infobox__icon.mr-3]
    [:span.infobox__message
     (loc/tr :multiple-investor-profiles
         [(cu/str-trim-lcase (loc/tr entity-type))
          (count related-entities)])]]
   [:table.zap-table.w-100
    [:tbody.zap-table__body
     (->> related-entities
          (map (fn [entity]
                 ^{:key (str "investor-legal-entity-" (:db/id entity))}
                 [:tr.zap-table__row
                  [:td.zap-table__cell.clickable
                   [:a
                    {:on-click #(routing/open-entity (:db/id entity))}
                    (:investor/name entity)]]])))]]])

(defn direct-investor-profile-activation?
  [{:keys [entity relationship-attribute reverse-relationship?]}]
  (and reverse-relationship?
       (= relationship-attribute :investor/legal-entity)
       (contains? #{:entity.type/company :entity.type/person}
                  (:entity/type entity))))

(defn direct-investor-profile-error-message
  [err]
  (or (:message err)
      (get-in err [:body :message])
      (some-> err .-message)
      (loc/tr :an-error-occurred)))

(defn create-investor-profile!
  [{:keys [entity state-atom]}]
  (let [entity-id (:db/id entity)
        error-atom (r/cursor state-atom [:error])
        notification-atom (r/cursor state-atom [:notification])
        create-data (merge
                     {:entity/type           :entity.type/investor
                      :investor/legal-entity entity-id
                      :investor/name         (entity/display-string entity :long)}
                     (when (f-permissions/field-write-access? :investor/owner
                                                              :entity.type/investor
                                                              nil)
                       {:investor/owner (:db/id @session/account)}))]
    (reset! error-atom nil)
    @error-atom
    (entity-actions/*->create
     create-data
     (fn [{:keys [body]}]
       (when-let [investor-id (:db/id body)]
         (swap! session/entities assoc investor-id body)
         (deref session/entities))
       (reset! notification-atom
               (loc/tr :entity-created-successfully
                   [(cu/str-trim-lcase (loc/tr :investor-profile))]))
       @notification-atom)
     (fn [err]
       (reset! notification-atom nil)
       @notification-atom
       (reset! error-atom
               (direct-investor-profile-error-message err))
       @error-atom
       (logging/error "Failed to create investor-profile" err)))))

(defn enrichment-module
  [{:keys [state _module-id _view-id _collapsed? _save-module-collapsed! _entities]}]
  (let [view-state (or state (r/atom {}))]
    (fn [{:keys [module-id view-id collapsed? save-module-collapsed! entities]}]
      (let [tab-options
            (->> entities
                 (keep (fn [ie]
                         (let [source-kw (:intelligence-data/source ie)]
                           {:tab-key   source-kw
                            :tab-label (loc/tr source-kw)})))
                 (filter (fn [{:keys [tab-key]}]
                           (case tab-key
                             :clearbit
                             (plan/available :feature/enrichment-clearbit @session/tenant)
                             :crunchbase
                             (plan/available :feature/enrichment-cruchbase @session/tenant)
                             true)))
                 (sort-by (fn [{:keys [tab-key]}]
                            (case tab-key
                              :clearbit 1
                              :crunchbase 2
                              3)))
                 (doall))]
        [:div
         {:class-name (fu/cs "entity-module discovery-module"
                             (when collapsed? "collapsed"))}
         [:div.entity-module-header
          {:on-click (fn []
                       (modules/toggle-collapse view-id
                                                module-id
                                                [view-id :modules :module-config module-id
                                                 :collapsed?]
                                                collapsed?
                                                save-module-collapsed!
                                                (not collapsed?)))}
          [:span
           [:i.fal.fa-exclamation-circle
            {:style {:color        "orange"
                     :margin-right "4px"}}]
           (loc/tr :enrich-module-header)]
          [:div.d-flex.d-sm-flex
           [:span.entity-module-header-content.d-flex
            (if collapsed?
              [:i.far.fa-chevron-right.collapse-icon]
              [:i.far.fa-chevron-down.collapse-icon])]]]
         [:div
          {:class-name (fu/cs "entity-module-content-container mt-4"
                              (when collapsed?
                                "collapsed"))}
          (if (cu/one-element? entities)
            [:p.text-sm.text-color--light-grey.mb-0.px-3.px-md-0
             (loc/tr :enrichment-text-1-source [(loc/tr (:intelligence-data/source (first entities)))])]
            [:<>
             [:p.text-sm.text-color--light-grey.mb-0.px-3.px-md-0
              (loc/tr :enrichment-text-multiple-sources)]
             [tab-strip/component
              {:on-change!      (fn [tab-key]
                                  (swap! view-state assoc :selected-tab tab-key)
                                  (deref view-state))
               :tabs            tab-options
               :tab-strip-id    (str "enrichment-data-" view-id)
               :default-tab-key (some->> tab-options
                                         (first)
                                         :tab-key)}]])
          [enrichment/view entities
           (if (cu/one-element? entities)
             (->> entities
                  (first)
                  :intelligence-data/source)
             (:selected-tab @view-state))]]]))))

(defn entity-relation-module
  [{:keys [state-atom module-id view-id module-label header-tools entity relationship-attribute
           ordering order-config-path reverse-relationship? override-editor-types header-fields
           forced-entity-types pipeline-id remove-fields hide-when-empty?]}]
  (let [tenant-schema @session/tenant-schema
        capman-sales-oppo-customer? (and (tenant-customizations/capman?)
                                         (= relationship-attribute :sales-opportunity/customer))
        config-path [view-id :modules :module-config module-id :collapsed?]
        relation-values (let [entity-type (->> relationship-attribute
                                               (registry/get-field-schema tenant-schema)
                                               :containing-entity-type)]
                          (->> @(r/track session/get-entities entity-type)
                               (cu/only-entity relationship-attribute (:db/id entity))))
        relation-value (if-not reverse-relationship?
                         (get entity relationship-attribute)
                         (->> relation-values
                              (first)
                              :db/id))
        investor-legal-entities-table? (and (= relationship-attribute :investor/legal-entity)
                                            (cu/multiple? relation-values))
        immediate-investor-creation? (direct-investor-profile-activation?
                                      {:entity                 entity
                                       :relationship-attribute relationship-attribute
                                       :reverse-relationship?  reverse-relationship?})
        entity-type (:entity/type entity)]
    (when (or relation-value
              (not hide-when-empty?))
      (if-not relation-value
        (when-not (session/is-read-only-user?)
          {:module-id              module-id
           :header                 module-label
           :collapsed?             @(r/track session/get-view-config config-path)
           :save-module-collapsed! (r/partial save-module-collapsed! config-path)
           :content
           [:div
            [:div
             (when-not (session/is-read-only-user?)
               [button/secondary-open
                {:class    "underline"
                 :on-click (fn [_]
                             (if immediate-investor-creation?
                               (create-investor-profile!
                                {:entity     entity
                                 :state-atom state-atom})
                               (let [title (cond
                                             capman-sales-oppo-customer?
                                             (loc/tr :add- [(loc/tr :investor-lc)])
                                             :else
                                             (loc/tr :add- [(loc/tr relationship-attribute)]))
                                     type-label
                                     (cond
                                       capman-sales-oppo-customer?
                                       (loc/tr :target-type [(loc/tr :investor)])

                                       (contains?
                                        #{:sale-agreement/customer
                                          :sales-opportunity/customer}
                                        relationship-attribute)
                                       (loc/tr :select-investor-legal-entity-type)

                                       :else
                                       (loc/tr :target-type
                                           [(or
                                             (loc/tr relationship-attribute)
                                             (when-let [fs (registry/get-field-schema
                                                            @session/tenant-schema
                                                            relationship-attribute)]
                                               (loc/field-name relationship-attribute
                                                               fs)))]))]
                                 (modals/open-modal!
                                  single-reference-relationship/modal-key
                                  {:title                  title
                                   :source-entity-id       (:db/id entity)
                                   :type-label             type-label
                                   :forced-entity-types    forced-entity-types
                                   :pipeline-id            pipeline-id
                                   :relationship-attribute relationship-attribute
                                   :reverse-relationship?  reverse-relationship?}))))}
                (if immediate-investor-creation?
                  (loc/tr :add- [(cu/str-trim-lcase (loc/tr :investor-profile))])
                  (loc/tr :button/add-related-entity))])]]})
        (if (= relationship-attribute :fundraising-opportunity/investor)
          (let [investor (session/get-entity relation-value)
                legal-entity-id (:investor/legal-entity investor)]
            {:module-id              module-id
             :header                 module-label
             :header-tools           header-tools
             :collapsed?             @(r/track session/get-view-config config-path)
             :save-module-collapsed! (r/partial save-module-collapsed! config-path)
             :content
             [:div
              [entities-editor/view
               {:state-atom       state-atom
                :entity-ids       [relation-value legal-entity-id]
                :view-id          view-id
                :module-id        module-id
                :remove-fields
                (merge {relation-value (concat [:common/network-drive :common/named-network-drives]
                                               (field/entity-tag-fields @session/tenant-schema
                                                                        (:entity/type investor))
                                               header-fields)}
                       (when legal-entity-id
                         (let [legal-entity (get @session/entities legal-entity-id)
                               entity-fields (->> legal-entity
                                                  :entity/type
                                                  (registry/get-schema @session/tenant-schema)
                                                  (map first)
                                                  (set))]
                           (if (= (:entity/type legal-entity) :entity.type/company)
                             {legal-entity-id (->> (set/difference entity-fields
                                                                   #{:asset/street-address
                                                                     :asset/street-address-2
                                                                     :asset/country
                                                                     :asset/state
                                                                     :asset/city
                                                                     :asset/zip-code
                                                                     :company/website
                                                                     :company/phone})
                                                   (into []))}
                             {legal-entity-id (->> (set/difference entity-fields
                                                                   #{:person/universal-email-address
                                                                     :person/last-name
                                                                     :person/first-name
                                                                     :person/universal-phone-number
                                                                     :person/location-address
                                                                     :person/location-city
                                                                     :person/location-state
                                                                     :person/location-country
                                                                     :person/location-zip-code})
                                                   (into []))}))))
                :default-ordering (concat
                                   (->> [:investor/name
                                         :investor/legal-entity
                                         :investor/type
                                         :investor/investor-type
                                         :investor/interest-in-products
                                         :investor/preferred-currency
                                         :investor/allocation-split]
                                        (map (fn [a]
                                               [relation-value a]))
                                        (into []))
                                   (->> [:asset/street-address
                                         :asset/street-address-2
                                         :asset/country
                                         :asset/state
                                         :asset/city
                                         :asset/zip-code
                                         :company/website
                                         :company/phone]
                                        (keep (fn [a]
                                                (when legal-entity-id
                                                  [legal-entity-id a]))))
                                   (->> [:person/universal-email-address
                                         :person/last-name
                                         :person/first-name
                                         :person/universal-phone-number
                                         :person/location-address
                                         :person/location-city
                                         :person/location-state
                                         :person/location-country
                                         :person/location-zip-code]
                                        (keep (fn [a]
                                                (when legal-entity-id
                                                  [legal-entity-id a])))))
                :ordering         ordering}]]})
          {:module-id              module-id
           :header                 module-label
           :header-tools           header-tools
           :collapsed?             @(r/track session/get-view-config config-path)
           :save-module-collapsed! (r/partial save-module-collapsed! config-path)
           :content                [:div
                                    (if investor-legal-entities-table?
                                      [investor-legal-entities-table relation-values entity-type]
                                      [entity-editor/view
                                       {:state-atom            state-atom
                                        :entity-id             relation-value
                                        :containing-entity     entity
                                        :remove-fields         remove-fields
                                        :override-editor-types override-editor-types
                                        :view-id               view-id
                                        :ordering              ordering
                                        :order-config-path     order-config-path
                                        :module-id             module-id}])]})))))

(defn header-tools-remove-icon
  [remove-tooltip remove!]
  [button/secondary-open
   {:tooltip remove-tooltip}
   [:i.fal.fa-trash-alt
    {:on-click remove!}]])

(defn remove-attribute-icon
  [{:keys [entity attribute tooltip-text confirmation-body]}]
  (let [attribute-label (loc/field-name attribute
                                        (registry/get-field-schema @session/tenant-schema
                                                                   attribute))
        tooltip-text (or tooltip-text (loc/tr :remove- [(cu/str-trim-lcase attribute-label)]))
        remove-tooltip tooltip-text
        confirmation-body (or confirmation-body (loc/tr :remove- [(cu/str-trim-lcase attribute-label)]))
        remove! (fn [_]
                  (modals/open-modal!
                    delete-confirmation/modal-key
                    {:body       confirmation-body
                     :on-delete! (fn [_]
                                   (entity-actions/*->field-update!
                                     (:db/id entity)
                                     attribute
                                     nil
                                     identity
                                     (fn [_err]
                                       (logging/error "Failed to remove" attribute-label))))}))]
    [header-tools-remove-icon remove-tooltip remove!]))

(defn audit-trail-button
  [{:keys [entity entity-ids]}]
  (when-not (session/is-read-only-user?)
    (let [entity-id (:db/id entity)
          entity-ids (or entity-ids [entity-id])
          entity-name (entity/display-string entity :short session/get-entity)
          modal-title (loc/tr :audit-trail-for-entity [entity-name])]
      (zap-toolbar/dropdown-button
        {:icon      :fa-history
         :on-select (fn []
                      (modals/open-modal!
                        audit-trail-modal/modal-key
                        {:title               modal-title
                         :entity-ids          entity-ids
                         :primary-entity-type (:entity/type entity)
                         :primary-entity-id   entity-id}))
         :label     (loc/tr :audit-trail)}))))
